<template>
  <v-card variant="outlined" class="pa-4">
    <!-- Label input field and add button -->
    <div class="d-flex align-center mb-4">
      <v-text-field
        v-model="newLabel"
        @keyup.enter="addLabel"
        class="mr-4"
        variant="solo-filled"
        hide-details="auto"
        :error-messages="errorMessage"
        :label="$t('menus.uploadFiles.label')"
        flat
      />

      <div style="align-self: flex-start;">
        <v-btn
          color="secondaryButtons"
          @click="addLabel"
          variant="flat"
          prepend-icon="mdi-plus-circle"
        >
          {{ $t('menus.uploadFiles.addLabel') }}
        </v-btn>
      </div>

    </div>

    <v-container>
      <v-row>
        <!-- Treeview column -->
        <v-col cols="12" md="6">
          <v-treeview
            v-model="selectedLabels"
            v-if="hasLabelCategories"
            :items="categories"
            item-title="name"
            item-value="value"
            :label="$t('routes.pod.menuEditLabels.selectCategory')"
            select-strategy="classic"
            density="comfortable"
            selectable
            return-object
            open-all
          >
            <template v-slot:toggle="{ props, isOpen }">
              <v-btn
                v-bind="props"
                variant="text"
                density="comfortable"
                :icon="isOpen ? '$treeviewCollapse' : '$treeviewExpand'"
                :aria-label="isOpen ? $t('common.collapse') : $t('common.expand')"
                size="32"
              />
            </template>

            <template v-slot:item="{ props, item }">
              <v-treeview-item v-bind="props" :value="item">
                <template v-slot:prepend="{ select, isSelected }">
                  <v-checkbox-btn
                    :model-value="isSelected"
                    @update:model-value="(v) => select(v)"
                    :aria-label="$t('common.toggle')"
                  />
                </template>
              </v-treeview-item>
            </template>

            <template v-slot:header="{ props, item }">
              <v-treeview-item v-bind="props" :value="item">
                <template v-slot:toggle="{ props, isOpen }">
                  <v-btn
                    v-bind="props"
                    variant="text"
                    density="comfortable"
                    :icon="isOpen ? '$treeviewCollapse' : '$treeviewExpand'"
                    :aria-label="isOpen ? $t('common.collapse') : $t('common.expand')"
                    size="32"
                  />
                </template>

                <template v-slot:prepend="{ select, isSelected, isIndeterminate }">
                  <v-checkbox-btn
                    :model-value="isSelected"
                    @update:model-value="(v) => select(v)"
                    :aria-label="$t('common.toggle')"
                    :indeterminate="isIndeterminate"
                  />
                </template>
              </v-treeview-item>
            </template>
          </v-treeview>
        </v-col>

        <!-- Chips container column -->
        <v-col cols="12" md="6">
          <!-- Displaying labels as chips -->
          <div class="d-flex flex-wrap">
            <v-chip
              v-for="(item) in labels"
              :key="item.id"
              closable
              @click:close="removeLabel(item)"
              variant="flat"
              class="ma-2"
              rounded="lg"
            >
              {{ item.name }}
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useLabelStore } from '@/stores/LabelStore.js'

export default {
  emits: ["labelsChanged"],
  name: "UploadDialogCustomLabels",

  data() {
    return {
      newLabel: '',
      labels: [],
      categories: [],
      selectedLabels: [],
      errorMessage: '',
      flatItems: [],
      emitTimeout: null,
    };
  },
  beforeUnmount() {
    if (this.emitTimeout) {
      clearTimeout(this.emitTimeout);
    }
  },
  watch: {
    selectedLabels: {
      handler(newItems, oldItems) {
        // Process newly selected items
        const newLabelsToAdd = newItems
          .filter((selectedItem) => (
            selectedItem.isLabel &&
            !this.labels.some((label) => label.id === selectedItem.id)
          ))
          .map((item) => ({ id: item.id, name: item.name }));

        if (newLabelsToAdd.length > 0) {
          this.labels.push(...newLabelsToAdd);
        }

        // Process removed items
        const removedItemIds = oldItems
          .filter((oldItem) => (
            oldItem.isLabel &&
            !newItems.some((newItem) => newItem.id === oldItem.id)
          ))
          .map((item) => item.id);

        if (removedItemIds.length > 0) {
          this.labels = this.labels.filter((label) => !removedItemIds.includes(label.id));
        }
      },
      deep: true
    },

    labels: {
      handler(newLabels) {
        // Debounce the emit to avoid excessive event firing
        clearTimeout(this.emitTimeout);
        this.emitTimeout = setTimeout(() => {
          this.$emit('labelsChanged', [...newLabels]);
        }, 300);
      },
      deep: true
    }
  },

  mounted() {
    this.resetState();

    if (this.categories.length === 0) {
      this.loadCategoriesWithLabels();
    }
  },

  methods: {
    addLabel() {
      this.errorMessage = '';
      const newLabelName = this.newLabel.trim();

      if (newLabelName !== '') {
        const labelExists = this.labels.some((label) => label.name === newLabelName);

        if (labelExists) {
          this.errorMessage = this.$t('menus.uploadFiles.labelAlreadyExists');
        } else {
          const existingLabel = this.flatItems.find((item) => item.name === newLabelName);
          if (existingLabel) {
            this.selectedLabels.push(existingLabel);
          } else {
            const newId = `custom-${Date.now()}`;
            this.labels.push({
              id: newId,
              name: newLabelName
            });
          }
        }
      }

      this.newLabel = '';
    },
    removeLabel(item) {
      const hasSelectedLabel = this.selectedLabels.some((selectedLabel) => selectedLabel.id === item.id);

      if (hasSelectedLabel) {
        this.selectedLabels = this.selectedLabels.filter((selectedItem) => (
          !selectedItem.isLabel || selectedItem.id !== item.id
        ));
      }

      this.labels = this.labels.filter((label) => label.id !== item.id);
    },
    loadCategoriesWithLabels: function() {
      const payload = {
        accessPointId: this.accessPointId,
        includeLabels: true,
      };

      const labelStore = useLabelStore();
      labelStore.refreshCategories(payload)
        .then((categories) => {
          this.categories = categories;

          let flatItems = [];

          let stack = this.categories.slice();
          while (stack.length) {
            let node = stack.shift();
            flatItems.push(node);
            stack = stack.concat(node.children || []);
          }

          this.flatItems = flatItems;
        });
    },
    resetState() {
      this.labels = [];
      this.selectedLabels = [];
      this.errorMessage = '';
    }
  },

  computed: {
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    hasLabelCategories() {
      return this.categories && this.categories.length > 0;
    },
  }
};
</script>
